import { TRIP_TRANSITIONS, type VehiclePositionEvent } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, FileText, Flag, Pencil, Play, Plus, Send, Square } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Badge, Dialog, Field, PageHeader, Skeleton, Spinner, useToast } from '../../components/ui';
import { ExpenseFormDialog } from '../expenses/ExpenseFormDialog';
import { catName, expenseTone, type ExpenseRow } from '../expenses/types';
import { api, qs, type Paginated } from '../../lib/api';
import { fmtDate, fmtDateTime, fmtDuration, fmtEgp, fmtNumber } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useSocketEvent } from '../../lib/socket';
import { MapView } from '../../map/MapView';
import { MarkersLayer, type PointMarker } from '../../map/MarkersLayer';
import { RouteLayer } from '../../map/RouteLayer';
import { VehiclesLayer, vehicleKind, type VehicleMarker } from '../../map/VehiclesLayer';
import { dispatchTone, pointLabel, tripTone, type TripDetail, type TripRoute } from './types';

type Action = 'dispatch' | 'start' | 'end' | 'cancel' | null;

export function TripDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const can = useSession((s) => s.can);
  const [action, setAction] = useState<Action>(null);
  const [form, setForm] = useState<{ odometerKm: string; reason: string; receiver: string; notes: string }>({ odometerKm: '', reason: '', receiver: '', notes: '' });
  const [livePos, setLivePos] = useState<VehiclePositionEvent | null>(null);
  const [addingExpense, setAddingExpense] = useState(false);
  const expenses = useQuery({ queryKey: ['expenses', 'trip', id], queryFn: () => api<Paginated<ExpenseRow>>(`/expenses${qs({ tripId: id, pageSize: 50 })}`).then((r) => r.data), enabled: can('expenses:read') });
  useSocketEvent('expense.updated', useCallback(() => void qc.invalidateQueries({ queryKey: ['expenses', 'trip', id] }), [id, qc]));

  const q = useQuery({ queryKey: ['trip', id], queryFn: () => api<TripDetail>(`/trips/${id}`) });
  const trip = q.data;
  const active = trip?.status === 'IN_PROGRESS' || trip?.status === 'PAUSED';
  const route = useQuery({ queryKey: ['trip-route', id, trip?.status], enabled: !!trip?.startedAt, queryFn: () => api<TripRoute>(`/trips/${id}/route`), refetchInterval: active ? 30_000 : false });

  useSocketEvent(
    'trip.updated',
    useCallback(
      (e: { tripId: string }) => {
        if (e.tripId === id) void qc.invalidateQueries({ queryKey: ['trip', id] });
      },
      [id, qc],
    ),
  );
  useSocketEvent(
    'vehicle.position',
    useCallback(
      (e: VehiclePositionEvent) => {
        if (active && e.vehicleId === trip?.vehicleId) setLivePos(e);
      },
      [active, trip?.vehicleId],
    ),
  );

  const markers = useMemo<PointMarker[]>(() => {
    if (!trip) return [];
    const out: PointMarker[] = [];
    if (trip.originLat != null && trip.originLon != null) out.push({ id: 'o', lat: trip.originLat, lon: trip.originLon, label: pointLabel(trip, 'origin') ?? t('trips.origin'), glyph: 'A', color: '#0F3D26' });
    trip.stops.forEach((s) => {
      if (s.lat != null && s.lon != null) out.push({ id: s.id, lat: s.lat, lon: s.lon, label: s.placeName ?? s.addressText ?? `${t('trips.stop')} ${s.seq}`, glyph: String(s.seq), color: s.status === 'DEPARTED' ? '#16A34A' : '#2563EB' });
    });
    if (trip.destLat != null && trip.destLon != null) out.push({ id: 'd', lat: trip.destLat, lon: trip.destLon, label: pointLabel(trip, 'dest') ?? t('trips.dest'), glyph: 'B', color: '#DB2777' });
    return out;
  }, [trip, t]);
  const vehicleMarker = useMemo<VehicleMarker[]>(() => {
    if (!trip?.vehicle || !active) return [];
    const last = livePos ?? (route.data?.points.length ? { ...route.data.points[route.data.points.length - 1]!, speedKph: null, heading: null } : null);
    return last ? [{ id: trip.vehicle.id, label: trip.vehicle.plateAr, lat: last.lat, lon: last.lon, heading: last.heading ?? null, status: 'MOVING', speedKph: last.speedKph ?? null, kind: vehicleKind(trip.vehicle.type) }] : [];
  }, [trip, active, livePos, route.data]);

  const run = useMutation({
    mutationFn: async (a: Exclude<Action, null>) => {
      const odo = form.odometerKm ? Number(form.odometerKm) : null;
      const body =
        a === 'dispatch' ? { message: form.notes || undefined } : a === 'start' ? { odometerKm: odo, notes: form.notes || null } : a === 'end' ? { odometerKm: odo, podReceiverName: form.receiver || null, endNotes: form.notes || null } : { reason: form.reason || undefined };
      return api<TripDetail>(`/trips/${id}/${a}`, { method: 'POST', json: body });
    },
    onSuccess: () => {
      toast.success(t('common.saved'));
      setAction(null);
      setForm({ odometerKm: '', reason: '', receiver: '', notes: '' });
      void qc.invalidateQueries({ queryKey: ['trip', id] });
      void qc.invalidateQueries({ queryKey: ['trips'] });
    },
    onError: toast.error,
  });
  const simple = useMutation({
    mutationFn: (a: 'pause' | 'resume') => api(`/trips/${id}/${a}`, { method: 'POST', json: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['trip', id] }),
    onError: toast.error,
  });

  if (q.isLoading || !trip) return <Skeleton className="h-96" />;
  const allowed = TRIP_TRANSITIONS[trip.status];
  const openAction = (a: Exclude<Action, null>) => {
    setForm({ odometerKm: trip.suggestedOdometerKm != null ? String(trip.suggestedOdometerKm) : '', reason: '', receiver: '', notes: '' });
    setAction(a);
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span dir="ltr">{trip.number}</span>
            <Badge tone={tripTone(trip.status)} dot>
              {t(`trips.statuses.${trip.status}`)}
            </Badge>
            {trip.dispatchStatus !== 'NONE' ? <Badge tone={dispatchTone(trip.dispatchStatus)}>{t(`trips.dispatch.${trip.dispatchStatus}`)}</Badge> : null}
          </span>
        }
        subtitle={`${pointLabel(trip, 'origin') ?? '—'} ← ${pointLabel(trip, 'dest') ?? '—'}`}
        actions={
          <>
            {trip.customer && trip.status === 'COMPLETED' && can('invoices:create') ? (
              <Link className="btn-secondary" to={`/invoices/new?tripId=${id}`}>
                <FileText size={16} aria-hidden /> {t('trips.createInvoice')}
              </Link>
            ) : null}
            {allowed.includes('update') && can('trips:update') ? (
              <Link className="btn-secondary" to={`/trips/${id}/edit`}>
                <Pencil size={16} aria-hidden /> {t('common.edit')}
              </Link>
            ) : null}
            {allowed.includes('dispatch') && can('trips:dispatch') && trip.dispatchStatus !== 'PENDING' ? (
              <button className="btn-secondary" onClick={() => openAction('dispatch')}>
                <Send size={16} aria-hidden /> {t('trips.actions.dispatch')}
              </button>
            ) : null}
            {allowed.includes('start') && can('trips:update') ? (
              <button className="btn-primary" onClick={() => openAction('start')}>
                <Play size={16} aria-hidden /> {t('trips.actions.start')}
              </button>
            ) : null}
            {allowed.includes('pause') && can('trips:update') ? (
              <button className="btn-secondary" onClick={() => simple.mutate('pause')}>
                {t('trips.actions.pause')}
              </button>
            ) : null}
            {allowed.includes('resume') && can('trips:update') ? (
              <button className="btn-secondary" onClick={() => simple.mutate('resume')}>
                {t('trips.actions.resume')}
              </button>
            ) : null}
            {allowed.includes('end') && can('trips:update') ? (
              <button className="btn-primary" onClick={() => openAction('end')}>
                <Flag size={16} aria-hidden /> {t('trips.actions.end')}
              </button>
            ) : null}
            {allowed.includes('cancel') && can('trips:update') ? (
              <button className="btn-ghost text-danger" onClick={() => openAction('cancel')}>
                <Ban size={16} aria-hidden /> {t('trips.actions.cancel')}
              </button>
            ) : null}
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="card min-h-[380px] overflow-hidden !p-0 lg:h-[520px]">
          <MapView zoom={6} className="h-full">
            <MarkersLayer markers={markers} fit={!route.data?.points.length} />
            <RouteLayer points={route.data?.points ?? []} fit={!!route.data?.points.length && !livePos} />
            <VehiclesLayer vehicles={vehicleMarker} />
          </MapView>
        </div>

        <div className="flex flex-col gap-4">
          <section className="card">
            <h2 className="mb-3 text-sm font-bold text-text-2">{t('trips.assignment')}</h2>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <Row label={t('trips.vehicle')}>{trip.vehicle ? <Link className="hover:underline" to={`/vehicles/${trip.vehicle.id}`}>{trip.vehicle.plateAr}</Link> : '—'}</Row>
              <Row label={t('trips.driver')}>{trip.driver ? <Link className="hover:underline" to={`/drivers/${trip.driver.id}`}>{trip.driver.fullName}</Link> : '—'}</Row>
              <Row label={t('trips.customer')}>{trip.customer?.name ?? '—'}</Row>
              <Row label={t('trips.shipmentOrderNo')}>{trip.shipmentOrderNo ?? '—'}</Row>
              <Row label={t('trips.cargoType')}>{trip.cargoType ?? '—'}</Row>
              <Row label={t('trips.scheduledAt')}>{fmtDateTime(trip.scheduledAt, i18n.language) || '—'}</Row>
            </dl>
          </section>

          <section className="card">
            <h2 className="mb-3 text-sm font-bold text-text-2">{t('trips.summary')}</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Kpi label={t('trips.distance')} value={trip.distanceKm != null ? `${fmtNumber(trip.distanceKm, i18n.language, 1)} ${t('units.km')}` : '—'} hint={trip.distanceSource ? t(`trips.distanceSource.${trip.distanceSource}`) : undefined} />
              <Kpi label={t('trips.duration')} value={trip.durationSec != null ? fmtDuration(trip.durationSec, i18n.language) : active && trip.startedAt ? fmtDuration(Math.round((Date.now() - new Date(trip.startedAt).getTime()) / 1000), i18n.language) : '—'} />
              <Kpi label={t('trips.maxSpeed')} value={trip.maxSpeedKph != null ? `${fmtNumber(trip.maxSpeedKph, i18n.language)} ${t('units.kph')}` : '—'} />
              <Kpi label={t('trips.odometer')} value={trip.startOdometerKm != null ? `${fmtNumber(trip.startOdometerKm, i18n.language)} → ${trip.endOdometerKm != null ? fmtNumber(trip.endOdometerKm, i18n.language) : '…'}` : '—'} />
            </div>
            {trip.podReceiverName || trip.endNotes ? (
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-border-soft pt-3 text-sm">
                <Row label={t('trips.podReceiver')}>{trip.podReceiverName ?? '—'}</Row>
                <Row label={t('trips.endNotes')}>{trip.endNotes ?? '—'}</Row>
              </dl>
            ) : null}
          </section>

          {trip.stops.length ? (
            <section className="card">
              <h2 className="mb-3 text-sm font-bold text-text-2">{t('trips.stops')}</h2>
              <ol className="flex flex-col gap-2">
                {trip.stops.map((s) => (
                  <li key={s.id} className="flex items-center gap-3 text-sm">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-bold text-brand-ink">{s.seq}</span>
                    <span className="flex-1 truncate">{s.placeName ?? s.addressText ?? '—'}</span>
                    <Badge tone={s.status === 'DEPARTED' ? 'success' : s.status === 'ARRIVED' ? 'info' : s.status === 'SKIPPED' ? 'neutral' : 'warning'}>{t(`trips.stopStatus.${s.status}`)}</Badge>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}

          {can('expenses:read') ? (
            <section className="card">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-bold text-text-2">{t('trips.expenses')}{expenses.data?.length ? <span className="ms-2 text-xs font-normal text-text-muted">{fmtEgp(expenses.data.filter((e) => e.status === 'APPROVED').reduce((a, e) => a + Number(e.amountEgp), 0), i18n.language)}</span> : null}</h2>
                {can('expenses:create') ? <button className="btn-ghost !min-h-8 text-brand-ink" onClick={() => setAddingExpense(true)}><Plus size={14} /> {t('expenses.add')}</button> : null}
              </div>
              {expenses.data?.length ? (
                <ul className="flex flex-col gap-2 text-sm">
                  {expenses.data.map((e) => (
                    <li key={e.id} className="flex items-center gap-2">
                      <Link to={`/expenses/${e.id}`} className="flex-1 truncate hover:underline">{catName(e.category, i18n.language)} · <span className="text-text-muted">{fmtDate(e.occurredAt, i18n.language)}</span></Link>
                      <span className="font-semibold tabular-nums">{fmtEgp(e.amountEgp, i18n.language)}</span>
                      <Badge tone={expenseTone(e.status)}>{t(`expenses.statuses.${e.status}`)}</Badge>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-text-faint">{t('expenses.noneForTrip')}</p>}
            </section>
          ) : null}

          <section className="card">
            <h2 className="mb-3 text-sm font-bold text-text-2">{t('trips.timeline')}</h2>
            <ol className="relative flex flex-col gap-3 border-s border-border-soft ps-4 text-sm">
              {trip.events.map((e, i) => (
                <li key={i} className="relative">
                  <span className="absolute -start-[21px] top-1.5 size-2.5 rounded-full border-2 border-white bg-brand" aria-hidden />
                  <div className="font-semibold">{t(`trips.events.${e.type}`)}</div>
                  <div className="text-xs text-text-muted">{fmtDateTime(e.at, i18n.language)}</div>
                  {e.note ? <div className="text-xs text-text-2">{e.note}</div> : null}
                </li>
              ))}
            </ol>
          </section>
        </div>
      </div>
      <ExpenseFormDialog open={addingExpense} onClose={() => setAddingExpense(false)} prefill={{ vehicleId: trip.vehicleId ?? undefined, driverId: trip.driverId ?? undefined, tripId: id }} />

      <Dialog
        open={!!action}
        onClose={() => setAction(null)}
        title={action ? t(`trips.actions.${action}`) : ''}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setAction(null)}>
              {t('common.cancel')}
            </button>
            <button className={action === 'cancel' ? 'btn-primary !bg-danger' : 'btn-primary'} disabled={run.isPending} onClick={() => action && run.mutate(action)}>
              {run.isPending ? <Spinner /> : action === 'cancel' ? <Ban size={16} /> : action === 'end' ? <Square size={16} /> : <Check size={16} />} {action ? t(`trips.actions.${action}`) : ''}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {action === 'dispatch' ? <p className="text-sm text-text-2">{t('trips.dispatchHint', { driver: trip.driver?.fullName ?? '—' })}</p> : null}
          {action === 'start' || action === 'end' ? (
            <Field label={t('trips.odometerKm')} hint={trip.suggestedOdometerKm != null ? t('trips.odometerSuggested', { km: fmtNumber(trip.suggestedOdometerKm, i18n.language) }) : undefined}>
              {(fid) => <input id={fid} type="number" dir="ltr" className="input" value={form.odometerKm} onChange={(e) => setForm((p) => ({ ...p, odometerKm: e.target.value }))} />}
            </Field>
          ) : null}
          {action === 'end' ? <Field label={t('trips.podReceiver')}>{(fid) => <input id={fid} className="input" value={form.receiver} onChange={(e) => setForm((p) => ({ ...p, receiver: e.target.value }))} />}</Field> : null}
          {action === 'cancel' ? <Field label={t('trips.cancelReason')}>{(fid) => <input id={fid} className="input" value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} />}</Field> : null}
          {action !== 'cancel' ? <Field label={action === 'dispatch' ? t('trips.dispatchMessage') : t('vehicles.notes')}>{(fid) => <textarea id={fid} className="input min-h-16" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />}</Field> : null}
        </div>
      </Dialog>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-text-muted">{label}</dt>
      <dd className="truncate font-semibold">{children}</dd>
    </>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl bg-surface-2 p-3">
      <div className="text-xs text-text-muted">{label}</div>
      <div className="mt-1 text-base font-bold" dir="auto">
        {value}
      </div>
      {hint ? <div className="text-[11px] text-text-faint">{hint}</div> : null}
    </div>
  );
}
